import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '~/components/overlays/Modal';
import { ModalHeader } from '~/components/overlays/ModalHeader';
import { Button, ClipSpinner } from '~/components/ui';
import { ContactCard } from '~/components/cards/ContactCard';
import {
  computeAllSelected,
  useContactSelection,
} from '~/hooks/useContactSelection';
import { AddSellerContactPanel } from '~/components/common/AddSellerContactPanel';
import type { Contact, ContactDetails, ContactsStatus } from '~/utils/contact';

type SelectContactsModalProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  organizationId: string;
  contacts: ContactDetails[];
  contactsStatus?: ContactsStatus;
  initialContactIds: string[];
  lockedContactIds: string[];
  initialPrimaryContactId?: string;
  openAddFormWhenEmpty?: boolean;
  lockedLabel: string;
  primaryRequiredMessage: string;
  onConfirm: (selectedContactIds: string[], primaryContact: Contact) => void;
  isSubmitting?: boolean;
}>;

type ContactsSelectionProps = Required<
  Omit<
    SelectContactsModalProps,
    | 'isOpen'
    | 'title'
    | 'subtitle'
    | 'contactsStatus'
    | 'initialPrimaryContactId'
  >
> &
  Readonly<{ initialPrimaryContactId: string | undefined }>;

function ContactsSelection({
  onClose,
  organizationId,
  contacts,
  initialContactIds,
  lockedContactIds,
  initialPrimaryContactId,
  openAddFormWhenEmpty,
  lockedLabel,
  primaryRequiredMessage,
  onConfirm,
  isSubmitting,
}: ContactsSelectionProps) {
  const { t } = useTranslation('general');
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const [primaryContactId, setPrimaryContactId] = useState<string | undefined>(
    initialPrimaryContactId
  );
  const lockedIds = new Set(
    lockedContactIds.filter((id) =>
      contacts.some((contact) => contact.id === id)
    )
  );
  const { selectedContactIds, toggleContact, toggleSelectAll } =
    useContactSelection(
      contacts.filter((contact) => !lockedIds.has(contact.id)),
      initialContactIds
    );
  const notifiedContactIds = new Set([...selectedContactIds, ...lockedIds]);
  if (primaryContactId !== undefined) {
    notifiedContactIds.add(primaryContactId);
  }
  const allSelected = computeAllSelected(contacts, notifiedContactIds);
  const listedPrimaryContact = contacts.find(
    (contact) => contact.id === primaryContactId
  );
  const isPrimaryContactListed = listedPrimaryContact !== undefined;
  const [startsWithForm] = useState(
    openAddFormWhenEmpty && contacts.length === 0
  );

  const handleContactAdded = (contactId: string) => {
    if (isPrimaryContactListed) {
      toggleContact(contactId);
    } else {
      setPrimaryContactId(contactId);
    }
  };

  const contactList =
    contacts.length === 0 ? (
      <p className="text-sm text-neut-sec">
        {t('select_contacts_modal.no_contacts')}
      </p>
    ) : (
      <>
        <div className="px-1 pb-2 flex items-center gap-2 border-b border-neut-border">
          <input
            id="select-contacts-all"
            type="checkbox"
            className="h-4 w-4 cursor-pointer"
            checked={allSelected}
            onChange={() => toggleSelectAll(allSelected)}
          />
          <label
            htmlFor="select-contacts-all"
            className="text-sm font-medium w-full cursor-pointer"
          >
            {t('select_contacts_modal.select_all')}
          </label>
        </div>
        <ul className="mt-3 grid auto-rows-fr gap-2">
          {contacts.map((contact) => (
            <li key={contact.id}>
              <ContactCard
                contact={contact}
                selected={notifiedContactIds.has(contact.id)}
                onSelectedChange={() => toggleContact(contact.id)}
                selectDisabled={
                  contact.id === primaryContactId || lockedIds.has(contact.id)
                }
                actedLabel={lockedIds.has(contact.id) ? lockedLabel : undefined}
                primary={contact.id === primaryContactId}
                onPrimaryChange={(isPrimary) =>
                  setPrimaryContactId(isPrimary ? contact.id : undefined)
                }
              />
            </li>
          ))}
        </ul>
        {!isPrimaryContactListed && (
          <p className="text-sm text-neut-sec mt-3">{primaryRequiredMessage}</p>
        )}
      </>
    );

  const handleConfirm = () => {
    if (listedPrimaryContact) {
      onConfirm(Array.from(notifiedContactIds), listedPrimaryContact);
    }
  };

  return (
    <AddSellerContactPanel
      organizationId={organizationId}
      testId="add-contact-button"
      disabled={isSubmitting}
      initiallyOpen={startsWithForm}
      focusAfterSave={startsWithForm ? confirmButtonRef : undefined}
      onAdded={(contact) => handleContactAdded(contact.id)}
      onCancel={startsWithForm ? onClose : undefined}
    >
      {({ trigger, form }) => (
        <>
          <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-3">
            {form && startsWithForm && (
              <p className="text-sm text-neut-sec">
                {t('select_contacts_modal.no_contacts')}
              </p>
            )}
            {form ?? contactList}
          </div>
          {!form && (
            <div className="px-6 py-4 border-t border-neut-border flex flex-wrap items-center gap-3 shrink-0">
              {trigger}
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                {t('select_contacts_modal.cancel')}
              </Button>
              <Button
                ref={confirmButtonRef}
                variant="primary"
                onClick={handleConfirm}
                loading={isSubmitting}
                disabled={isSubmitting || !isPrimaryContactListed}
                data-testid="confirm-contacts-button"
              >
                {t('select_contacts_modal.confirm')}
              </Button>
            </div>
          )}
        </>
      )}
    </AddSellerContactPanel>
  );
}

export function SelectContactsModal({
  isOpen,
  onClose,
  title,
  subtitle,
  contactsStatus = 'loaded',
  initialPrimaryContactId,
  openAddFormWhenEmpty = false,
  isSubmitting = false,
  ...selectionProps
}: SelectContactsModalProps) {
  const { t } = useTranslation('general');

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose();
  };

  const renderBody = () => {
    if (contactsStatus === 'loading') {
      return (
        <div className="px-6 py-4">
          <ClipSpinner />
        </div>
      );
    }
    if (contactsStatus === 'failed') {
      return (
        <p className="px-6 py-4 text-sm text-error-600">
          {t('select_contacts_modal.load_failed')}
        </p>
      );
    }
    return (
      <ContactsSelection
        {...selectionProps}
        onClose={handleClose}
        initialPrimaryContactId={initialPrimaryContactId}
        openAddFormWhenEmpty={openAddFormWhenEmpty}
        isSubmitting={isSubmitting}
      />
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div
        className="bg-neut-light rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col"
        data-testid="select-contacts-modal"
      >
        <ModalHeader
          title={title}
          description={subtitle}
          onClose={handleClose}
          closeDisabled={isSubmitting}
        />
        {renderBody()}
      </div>
    </Modal>
  );
}
